import { useState } from 'react';
import { apiLogin, apiSignup } from '../api';
import { saveAuth } from '../auth';

const ACTIVITY_LEVELS = [
  { value: 'sedentary', label: 'Sedentary (little or no exercise)' },
  { value: 'light',     label: 'Light (1-3 days/week)' },
  { value: 'moderate',  label: 'Moderate (3-5 days/week)' },
  { value: 'active',    label: 'Active (6-7 days/week)' },
];

const inputStyle = {
  padding: '12px 14px', borderRadius: 12,
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'rgba(255,255,255,0.07)',
  color: '#fff', width: '100%',
  boxSizing: 'border-box', fontSize: 15,
  marginBottom: 12, outline: 'none',
};

const labelStyle = {
  fontSize: 12, color: 'rgba(255,255,255,0.5)',
  marginBottom: 6, display: 'block', fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: 0.5,
};

export default function AuthScreen({ onAuth }) {
  const [tab,     setTab]     = useState('login');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const [loginForm,  setLoginForm]  = useState({ email: '', password: '', remember: true });
  const [signupForm, setSignupForm] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    age: '', weight: '', height: '', gender: '',
    phone: '', address: '', activityLevel: 'moderate'
  });

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    if (!loginForm.email || !loginForm.password) { setError('Email and password are required'); return; }
    setLoading(true);
    try {
      const res = await apiLogin({ email: loginForm.email, password: loginForm.password });
      saveAuth(res.token, res.user, loginForm.remember);
      onAuth(res.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    }
    setLoading(false);
  }

  async function handleSignup(e) {
    e.preventDefault();
    setError('');
    if (!signupForm.name || !signupForm.email || !signupForm.password) {
      setError('Name, email and password are required'); return;
    }
    if (signupForm.password !== signupForm.confirmPassword) {
      setError('Passwords do not match'); return;
    }
    if (signupForm.password.length < 6) {
      setError('Password must be at least 6 characters'); return;
    }
    setLoading(true);
    try {
      const payload = {
        name: signupForm.name, email: signupForm.email,
        password: signupForm.password, activityLevel: signupForm.activityLevel,
      };
      if (signupForm.age)     payload.age     = parseFloat(signupForm.age);
      if (signupForm.weight)  payload.weight  = parseFloat(signupForm.weight);
      if (signupForm.height)  payload.height  = parseFloat(signupForm.height);
      if (signupForm.gender)  payload.gender  = signupForm.gender;
      if (signupForm.phone)   payload.phone   = signupForm.phone;
      if (signupForm.address) payload.address = signupForm.address;

      const res = await apiSignup(payload);
      saveAuth(res.token, res.user, true);
      onAuth(res.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed. Please try again.');
    }
    setLoading(false);
  }

  return (
    <div style={{
      maxWidth: 420, margin: '0 auto', padding: 24,
      fontFamily: "'Segoe UI', sans-serif",
      minHeight: '100vh', color: '#fff',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Background blobs */}
      <div style={{
        position: 'fixed', top: -80, right: -80, width: 260, height: 260,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(82,130,255,0.25) 0%, transparent 70%)',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'fixed', bottom: 50, left: -60, width: 220, height: 220,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(168,85,247,0.2) 0%, transparent 70%)',
        pointerEvents: 'none'
      }} />

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 36, paddingTop: 50, position: 'relative' }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>🥗</div>
        <h1 style={{
          margin: 0, fontSize: 28, fontWeight: 800,
          background: 'linear-gradient(135deg, #fff 0%, #a0c4ff 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
        }}>
          Diet Tracker
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.4)', margin: '8px 0 0', fontSize: 14 }}>
          Track your nutrition, reach your goals
        </p>
      </div>

      {/* Tab switcher */}
      <div style={{
        display: 'flex', marginBottom: 28, borderRadius: 14, overflow: 'hidden',
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.1)',
        position: 'relative',
      }}>
        {[['login', '🔑 Login'], ['signup', '✨ Sign Up']].map(([t, label]) => (
          <button key={t} onClick={() => { setTab(t); setError(''); }}
            style={{
              flex: 1, padding: '14px 12px', border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: 14, transition: 'all 0.2s',
              background: tab === t
                ? 'linear-gradient(135deg, #5282ff, #a855f7)'
                : 'transparent',
              color: tab === t ? '#fff' : 'rgba(255,255,255,0.4)',
              borderRadius: 12,
            }}>
            {label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.15)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 10, padding: '10px 14px',
          marginBottom: 16, color: '#fca5a5', fontSize: 14
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Login form */}
      {tab === 'login' && (
        <form onSubmit={handleLogin}>
          <label style={labelStyle}>Email</label>
          <input type="email" placeholder="your@email.com"
            value={loginForm.email}
            onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))}
            style={inputStyle} />

          <label style={labelStyle}>Password</label>
          <input type="password" placeholder="••••••••"
            value={loginForm.password}
            onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
            style={inputStyle} />

          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
            <input type="checkbox" id="remember" checked={loginForm.remember}
              onChange={e => setLoginForm(f => ({ ...f, remember: e.target.checked }))}
              style={{ marginRight: 8, width: 16, height: 16, accentColor: '#5282ff' }} />
            <label htmlFor="remember" style={{ fontSize: 14,
              color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
              Remember me for 30 days
            </label>
          </div>

          <button type="submit" disabled={loading} style={{
            width: '100%', padding: 16, borderRadius: 14, border: 'none',
            background: loading
              ? 'rgba(255,255,255,0.1)'
              : 'linear-gradient(135deg, #5282ff, #a855f7)',
            color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer',
            boxShadow: loading ? 'none' : '0 4px 20px rgba(82,130,255,0.4)',
          }}>
            {loading ? 'Logging in...' : '🔑 Login'}
          </button>

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14,
            color: 'rgba(255,255,255,0.4)' }}>
            No account?{' '}
            <span onClick={() => setTab('signup')}
              style={{ color: '#818cf8', cursor: 'pointer', fontWeight: 700 }}>
              Sign up free
            </span>
          </p>
        </form>
      )}

      {/* Signup form */}
      {tab === 'signup' && (
        <form onSubmit={handleSignup}>
          <label style={labelStyle}>Full Name *</label>
          <input placeholder="Onkaar Singh" value={signupForm.name}
            onChange={e => setSignupForm(f => ({ ...f, name: e.target.value }))}
            style={inputStyle} />

          <label style={labelStyle}>Email *</label>
          <input type="email" placeholder="your@email.com" value={signupForm.email}
            onChange={e => setSignupForm(f => ({ ...f, email: e.target.value }))}
            style={inputStyle} />

          <label style={labelStyle}>Password * (min 6 characters)</label>
          <input type="password" placeholder="••••••••" value={signupForm.password}
            onChange={e => setSignupForm(f => ({ ...f, password: e.target.value }))}
            style={inputStyle} />

          <label style={labelStyle}>Confirm Password *</label>
          <input type="password" placeholder="••••••••" value={signupForm.confirmPassword}
            onChange={e => setSignupForm(f => ({ ...f, confirmPassword: e.target.value }))}
            style={inputStyle} />

          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            margin: '8px 0 16px'
          }} />
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', margin: '0 0 16px' }}>
            Optional — helps us suggest your daily calorie goals
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Age</label>
              <input type="number" placeholder="28" value={signupForm.age}
                onChange={e => setSignupForm(f => ({ ...f, age: e.target.value }))}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Gender</label>
              <select value={signupForm.gender}
                onChange={e => setSignupForm(f => ({ ...f, gender: e.target.value }))}
                style={{ ...inputStyle, appearance: 'none' }}>
                <option value="" style={{ background: '#1a1a2e' }}>Select</option>
                <option value="male"   style={{ background: '#1a1a2e' }}>Male</option>
                <option value="female" style={{ background: '#1a1a2e' }}>Female</option>
                <option value="other"  style={{ background: '#1a1a2e' }}>Other</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Weight (kg)</label>
              <input type="number" placeholder="70" value={signupForm.weight}
                onChange={e => setSignupForm(f => ({ ...f, weight: e.target.value }))}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Height (cm)</label>
              <input type="number" placeholder="175" value={signupForm.height}
                onChange={e => setSignupForm(f => ({ ...f, height: e.target.value }))}
                style={inputStyle} />
            </div>
          </div>

          <label style={labelStyle}>Activity Level</label>
          <select value={signupForm.activityLevel}
            onChange={e => setSignupForm(f => ({ ...f, activityLevel: e.target.value }))}
            style={{ ...inputStyle, appearance: 'none' }}>
            {ACTIVITY_LEVELS.map(a => (
              <option key={a.value} value={a.value} style={{ background: '#1a1a2e' }}>
                {a.label}
              </option>
            ))}
          </select>

          <label style={labelStyle}>Phone (optional)</label>
          <input placeholder="+1 416 555 0123" value={signupForm.phone}
            onChange={e => setSignupForm(f => ({ ...f, phone: e.target.value }))}
            style={inputStyle} />

          <label style={labelStyle}>Address (optional)</label>
          <input placeholder="Brampton, ON" value={signupForm.address}
            onChange={e => setSignupForm(f => ({ ...f, address: e.target.value }))}
            style={inputStyle} />

          <button type="submit" disabled={loading} style={{
            width: '100%', padding: 16, borderRadius: 14, border: 'none',
            background: loading
              ? 'rgba(255,255,255,0.1)'
              : 'linear-gradient(135deg, #34d399, #059669)',
            color: '#fff', fontSize: 16, fontWeight: 700,
            cursor: 'pointer', marginTop: 4,
            boxShadow: loading ? 'none' : '0 4px 20px rgba(52,211,153,0.3)',
          }}>
            {loading ? 'Creating account...' : '✨ Create Account'}
          </button>

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14,
            color: 'rgba(255,255,255,0.4)' }}>
            Already have an account?{' '}
            <span onClick={() => setTab('login')}
              style={{ color: '#818cf8', cursor: 'pointer', fontWeight: 700 }}>
              Login
            </span>
          </p>
        </form>
      )}
    </div>
  );
}